// Shared in-memory cache for `/api/opportunities/{id}/detail`.
//
// Pre-extraction (PR #57 review F1): both `tag_popover.ts` and
// `tag_rationales_panel.ts` had their own `Map<oppId, Promise<detail>>`.
// A user opening the chip popover and then the partner-rationales side
// panel for the same opp would fire `/detail` twice. Hoisting the
// cache here so all consumers share one source of truth.
//
// Promise-cached (not value-cached) so concurrent callers awaiting
// the same opp share one inflight fetch. On error we evict so the
// next caller can retry; otherwise the rejected promise would stay
// stuck for the rest of the session.
//
// Lifetime: SPA session. No TTL — read-mostly tool, reclassifications
// are infrequent admin events. If staleness becomes a concern, add a
// `lastFetched` timestamp + 5-minute TTL here without touching
// consumers.

import { fetchOpportunityDetail } from "../api";
import type { OpportunityDetailFull } from "../types";

const detailCache = new Map<string, Promise<OpportunityDetailFull>>();

/** Lazy-fetch + cache an opportunity's full detail. Re-calls share an
 * inflight promise; failures evict the entry so a retry can refetch. */
export function getOpportunityDetail(oppId: string): Promise<OpportunityDetailFull> {
  let p = detailCache.get(oppId);
  if (!p) {
    p = fetchOpportunityDetail(oppId).catch((err) => {
      detailCache.delete(oppId);
      throw err;
    });
    detailCache.set(oppId, p);
  }
  return p;
}

/** Test-only: wipe the cache between tests. */
export function _clearOpportunityDetailCache(): void {
  detailCache.clear();
}
